/**
 * Public Navbar Component
 * Navigation bar for public pages (landing, contact, pricing, etc.)
 */

import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Menu, X, Terminal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useLogoImageSrc, usePlatformLogoSettings } from '@/hooks/useLogoSettings'
import { PUBLIC_NAVBAR_LOGO_CLASS } from '@/config/branding.config'
import { usePublicSessionNav } from '@/hooks/usePublicSessionNav'
import { UrlGenerator } from '@/utils/url-generator.util'

interface PublicNavbarProps {
  showLoginButton?: boolean
  /** When true, show only Logout in the nav (e.g. on select-context page). Logout clears tokens and redirects to home. */
  showLogoutOnly?: boolean
  /** When true, show only logo + "Need help?" (e.g. on login page). No nav links or auth buttons. */
  minimal?: boolean
  /** Hide Home / Features / Pricing / Book a Demo (desktop + mobile). Logo + right actions still show. */
  hidePublicNavLinks?: boolean
}

export function PublicNavbar({
  showLoginButton = true,
  showLogoutOnly = false,
  minimal = false,
  hidePublicNavLinks = false,
}: PublicNavbarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoggedIn, handleConsoleClick, handleLogout } = usePublicSessionNav({
    probeCookieSession: true,
  })

  // Platform logo only (trust/security on login and public pages)
  const { logoUrl, fallbackUrl } = usePlatformLogoSettings({ surface: 'navbar' })
  const { src: logoSrc, onError: onLogoError } = useLogoImageSrc(logoUrl, fallbackUrl)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const handleNavClick = (path: string) => {
    navigate(path)
  }

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/features', label: 'Features' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/contact', label: 'Book a Demo' },
  ]

  // Get theme navbar background if available
  const navbarBg = typeof window !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--theme-navbar-bg').trim() || undefined
    : undefined

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 w-full z-50 bg-[#FFFFFF] border-b border-[#E2E8F0]',
        isScrolled && 'shadow-sm'
      )}
      style={{
        backgroundImage: navbarBg || undefined,
        backgroundSize: navbarBg ? 'cover' : undefined,
        backgroundPosition: navbarBg ? 'center' : undefined,
        backgroundRepeat: navbarBg ? 'no-repeat' : undefined,
      }}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-20">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Left */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img
                src={logoSrc}
                alt="Vidhyantra Logo"
                width={220}
                height={44}
                className={PUBLIC_NAVBAR_LOGO_CLASS}
                decoding="async"
                onError={onLogoError}
              />
            </Link>
          </div>

          {/* Desktop Menu - Center (hidden when minimal or hidePublicNavLinks) */}
          {!minimal && !hidePublicNavLinks && (
          <div className="hidden md:flex items-center justify-center flex-1 gap-8">
            {navLinks.map((link) =>
              link.path.startsWith('/#') ? (
                <button
                  key={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={cn(
                    'text-sm font-medium no-underline hover:no-underline pb-1 border-b-2 outline-none ring-0 focus:ring-0 focus:outline-none',
                    isActive(link.path)
                      ? '!text-[#FF6700] font-semibold border-[#FF6700]'
                      : '!text-black hover:!text-[#FF6700]/80 border-transparent'
                  )}
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'text-sm font-medium no-underline hover:no-underline pb-1 border-b-2 outline-none ring-0 focus:ring-0 focus:outline-none',
                    isActive(link.path)
                      ? '!text-[#FF6700] font-semibold border-[#FF6700]'
                      : '!text-black hover:!text-[#FF6700]/80 border-transparent'
                  )}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
          )}

          {/* Right - Auth Buttons (hidden when minimal; show Need help? instead) */}
          {minimal ? (
            <div className="flex items-center flex-shrink-0">
              <Link
                to="/contact"
                className="text-sm font-medium text-muted-foreground hover:text-foreground no-underline"
              >
                Need help?
              </Link>
            </div>
          ) : (showLoginButton || showLogoutOnly) && (
            <div className="hidden md:flex items-center gap-4 flex-shrink-0">
              {showLogoutOnly ? (
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="!text-[#4082B4] hover:!text-[#4082B4]"
                >
                  Logout
                </Button>
              ) : !isLoggedIn ? (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="text-sm font-medium  py-2 px-4 text-black rounded-lg border border-[#01274D]"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => {
                      window.location.href = `${UrlGenerator.getFrontendBaseUrl()}/onboarding`
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-[#3366FF] rounded-lg hover:bg-[#2563EB]"
                  >
                    Get started
                  </button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={handleConsoleClick}
                    className="flex items-center gap-2 border border-transparent hover:border-slate-200 !text-[#4082B4] hover:!text-[#4082B4]"
                    title="Go to Console"
                  >
                    <Terminal size={18} />
                    <span className="hidden lg:inline">Console</span>
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className="!text-[#4082B4] hover:!text-[#4082B4]"
                  >
                    Logout
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Mobile Menu Button (when minimal, still show for Need help? on small screens) */}
          <button
            className="md:hidden p-2 !text-[#4082B4] hover:!text-[#4082B4]"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col gap-4">
              {minimal ? (
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className="text-sm font-medium py-2 text-muted-foreground hover:text-foreground"
                >
                  Need help?
                </Link>
              ) : hidePublicNavLinks ? null : (
                navLinks.map((link) =>
                link.path.startsWith('/#') ? (
                  <button
                    key={link.path}
                    onClick={() => {
                      handleNavClick(link.path)
                      setIsOpen(false)
                    }}
                    className={cn(
                      'text-left text-sm font-medium py-2 w-full no-underline hover:no-underline',
                      isActive(link.path) ? '!text-blue-600 font-semibold' : '!text-[#4082B4]'
                    )}
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      'text-sm font-medium py-2 no-underline hover:no-underline',
                      isActive(link.path) ? '!text-blue-600 font-semibold' : '!text-[#4082B4]'
                    )}
                  >
                    {link.label}
                  </Link>
                )
              )
            )
              }
              {!minimal && (showLoginButton || showLogoutOnly) && (
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  {showLogoutOnly ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        handleLogout()
                        setIsOpen(false)
                      }}
                      className="w-full justify-start text-[#0F172A] hover:text-[#2563EB]"
                    >
                      Logout
                    </Button>
                  ) : !isLoggedIn ? (
                    <>
                      <button
                        onClick={() => {
                          navigate('/login')
                          setIsOpen(false)
                        }}
                        className="text-left text-sm font-medium !text-[#4082B4] py-2 w-full"
                      >
                        Login
                      </button>
                      <button
                        onClick={() => {
                          setIsOpen(false)
                          window.location.href = `${UrlGenerator.getFrontendBaseUrl()}/onboarding`
                        }}
                        className="text-left px-4 py-2 text-sm font-medium text-white bg-[#3366FF] rounded-lg w-fit"
                      >
                        Get started
                      </button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          handleConsoleClick()
                          setIsOpen(false)
                        }}
                        className="w-full justify-start flex items-center gap-2 text-[#0F172A] hover:text-[#2563EB]"
                      >
                        <Terminal size={18} />
                        Console
                      </Button>
                      <Button 
                        variant="ghost" 
                        onClick={() => {
                          handleLogout()
                          setIsOpen(false)
                        }} 
                        className="w-full justify-start !text-[#4082B4] hover:!text-[#4082B4]"
                      >
                        Logout
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

